<template>
    <div class="c">
        <div class="product__filters flex -wrap -justify-center" :disabled="!loaded">
            <div class="product__search -flex-stretch" :class="{ '-is-open': searchOpened }" v-on-clickaway="hideSearchSuggestions">
                <span class="fa">&#xf002;</span>
                <input type="search" class="input -large"
                    v-model="filters.search"
                    :placeholder="$t('products.searchplaceholder')"
                    @focus="showSearchSuggestions = true"
                    @keyup="needToHideSearchSuggestions"
                    />

                <ul v-if="searchOpened" class="search__suggestions">
                    <li
                        v-for="(sug, index) in searchSuggestions"
                        :key="'sug_'+ index"
                        @click="searchText(sug)">
                        {{ sug }}
                    </li>
                </ul>
            </div>
            <span class="-flex-stretch"></span>
            <ul class="filter__tiles">
                <li>
                    <Tile :name="$t('products.category')" @open="onTileOpen('category')" :open="openedTile === 'category'" class="-large">
                        <template v-slot:value>{{ filter_categories }}</template>
                        <template v-slot:dropdown>
                            <CategorySelect v-model="filters.categories" :categories="categories" @close="onTileOpen('')"></CategorySelect>
                        </template>
                    </Tile>
                </li>
                <template v-for="(shortcut, index) in catShortcuts">
                    <li v-if="!filters.categories.includes(shortcut.id)" @click="filters.categories = [shortcut.id]" :key="'shortcut_' + index">
                        <div class="tile__label">
                            <div class="tile__value">
                                {{ inlinedata(shortcut, 'naam', currentlang) }}
                            </div>
                        </div>
                    </li>
                </template>
                <li v-if="$store.getters['Auth/isAuthenticated']">
                    <Tile :name="$t('products.price')" @open="onTileOpen('price')" :open="openedTile === 'price'">
                        <template v-slot:value>{{ filter_price }}</template>
                        <template v-slot:dropdown>
                            <h4>{{ $t('products.pricerange') }}</h4>
                            <VueSlider
                                v-model="filters.price"
                                :enable-cross="true"
                                :min="defaultPrices[0]"
                                :max="defaultPrices[1]"
                                :interval="priceRangeInterval"
                                >
                            </VueSlider>
                        </template>
                    </Tile>
                </li>
                <li>
                    <Tile :name="$t('products.size')" @open="onTileOpen('size')" :open="openedTile === 'size'">
                        <template v-slot:value>{{ filter_size }}</template>
                        <template v-slot:dropdown>
                            <h4>{{ $t('products.size') }}</h4>
                            <Multiselect
                                :allowClear="true"
                                :multiple="true"
                                :options="size_options"
                                :placeholder="$t('products.choose')"
                                label="name"
                                valueFormat="string"
                                v-model="filters.size"
                            ></Multiselect>
                        </template>
                    </Tile>
                </li>

            </ul>
        </div>
    </div>
</template>

<script>
import debounce from 'lodash.debounce';
import { mixin as clickaway } from 'vue-clickaway';
import Multiselect from '@bit/duncank.work.forms.multi-select';
import VueSlider from 'vue-slider-component';
import Tile from '@/components/TileDropdown.vue';
import CategorySelect from '@/views/products/CategorySelect.vue';
import 'vue-slider-component/theme/default.css';

export default {
    name: 'ProductFilters',
    components: {
        CategorySelect,
        Multiselect,
        Tile,
        VueSlider,
    },
    mixins: [clickaway],
    data() {
        return {
            openedTile: '',
            loaded: false,
            priceRangeInterval: 25,
            showSearchSuggestions: false,
        };
    },
    mounted() {
        this.$store.dispatch('Products/getFilters').then(() => {
            this.loaded = true;
        });
    },
    computed: {
        currentlang() {
            return this.$store.getters['Language/current'];
        },

        filters() {
            return this.$store.getters['Products/filters'];
        },

        filter_categories() {
            if (this.filters.categories.length) {
                return this.findCatName(this.filters.categories);
            }
            return this.$t('products.categories_all');
        },

        filter_price() {
            const from_price = this.$options.filters.priceformat(Math.floor(this.filters.price[0]), 0);
            const to_price = this.$options.filters.priceformat(Math.floor(this.filters.price[1]), 0);
            return `€ ${from_price},- ${this.$t('products.till')} ${to_price},-`;
        },

        filter_size() {
            if (this.filters.size.length) {
                const selectedSizes = this.size_options.filter(it => this.filters.size.includes(it.id)) || [];
                return selectedSizes.map(it => it.name).join(', ');
            }
            return this.$t('products.all');
        },

        size_options() {
            return this.$store.getters['Products/sizes'];
        },

        categories() {
            return this.$store.getters['Products/categories'] || [];
        },

        categories_flat() {
            const allCats = [...this.categories];
            this.categories.forEach((cat) => {
                if (cat.children && cat.children.length) {
                    allCats.push(...cat.children);
                }
            });
            return allCats;
        },

        catShortcuts() {
            const featured = this.categories_flat.filter(it => it.featured);
            return featured;
        },

        defaultPrices() {
            const prices = this.$store.getters['Products/defaultPrices'];
            const remaining = prices[1] % this.priceRangeInterval;
            if (remaining !== 0) {
                const newMax = prices[1] + (this.priceRangeInterval - remaining);
                prices[1] = Math.floor(newMax);
            }
            return prices;
        },

        searchSuggestions() {
            return this.$store.getters['Products/searchSuggestions'] || [];
        },

        searchOpened() {
            return this.showSearchSuggestions && this.searchSuggestions.length;
        },
    },
    methods: {
        needToHideSearchSuggestions() {
            if (this.filters.search && this.filters.search.length > 0) {
                this.showSearchSuggestions = false;
            } else {
                this.showSearchSuggestions = true;
            }
        },

        onTileOpen(val) {
            if (!val || this.openedTile === val) {
                this.openedTile = '';
                return false;
            }
            this.openedTile = val;
            return true;
        },

        findCatName(ids) {
            const selected = this.categories_flat.find(cat => ids.includes(cat.id));
            return this.inlinedata(selected, 'naam', this.currentlang);
        },

        // eslint-disable-next-line
        onFilterChange: debounce(function() {
            this.hideSearchSuggestions();
            const keys = Object.keys(this.filters);
            for (let i = 0; i < keys.length; i += 1) {
                const item = this.filters[keys[i]];
                if ((item && typeof item === 'string') || (Array.isArray(item) && item.length > 0)) {
                    if (!keys[i] === 'price' || !(item[0] === this.defaultPrices[0] && item[1] === this.defaultPrices[1])) {
                        // Get filtered results
                        this.$emit('filter', this.filters);
                        return false;
                    }
                }
            }

            // Switch to normal list
            this.$store.commit('Products/stopFiltering');
        }, 500),

        hideSearchSuggestions() {
            if (this.showSearchSuggestions) {
                setTimeout(() => {
                    this.showSearchSuggestions = false;
                }, 100);
            }
        },

        searchText(val) {
            console.log(val);
            this.filters.search = val;
        },
    },
    watch: {
        filters: {
            handler: 'onFilterChange',
            deep: true,
        },
    },
};
</script>

<style>
    .product__filters {
        width: 100%;
        max-width: 65rem;
        margin: 4rem auto;
        position: relative;
        z-index: 10;
    }
        .product__search {
            width: 100%;
            max-width: 40rem;
            margin: 0 auto 0.4rem;
            position: relative;
            z-index: 1;
        }
            .product__search.-is-open { z-index: 12; }
        .product__search input { padding-left: 4rem; margin: 0; position: relative; z-index: 20; }
        .product__search .fa {
            position: absolute;
            left: 1.6rem;
            top: 50%;
            margin-top: -0.9rem;
            z-index: 21;
            pointer-events: none;
            opacity: 0.5;
        }
    .filter__tiles { text-align: center; position: relative; z-index: 5; }
    .filter__tiles > li {
        display: inline-block;
        text-align: left;
    }

    .search__suggestions {
        background: white;
        border-radius: 0 0 var(--borderradius) var(--borderradius);
        box-shadow: var(--box-shadow);
        position: absolute;
        top: calc(100% - 0.4rem);
        left: 0.1rem;
        right: 0.1rem;
        padding-top: 0.8rem !important;
        z-index: 10;
        overflow-y: auto;
        max-height: 20rem;
    }
        .search__suggestions li {
            padding: var(--padding-s) var(--padding-m);
            transition: background .3s var(--easing-links);
            cursor: pointer;
            display: block;
        }
        .search__suggestions li:hover {
            background-color: var(--color-bg-darker);
        }
</style>
